import Sider from 'antd/lib/layout/Sider';
import classNames from 'classnames';
import { RefObject, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { FullscreenExitOutlined, FullscreenOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { PageLoader } from '@common/components/PageLoader/PageLoader';
import { CANVAS_OBJECT_TYPE_PREFIX, SELECTION_CIRCLES } from '@common/constants';
import { SectionTypes } from '@common/enums/section-type-enum';
import { useCardStep } from '@common/helpers';
import { Section, SectionReferences } from '@common/models/section-model';
import { isDevMode } from '@common/utils';
import useWindowSize from '@common/utils/useWindowSize';
import CheckoutButton from '@components/buttons/checkout-button/checkout-button';
import PriceBubbleButton from '@components/buttons/price-bubble-button/price-bubble-button';
import { useAnalyticsEvents } from '@core/hooks/useAnalyticsEvents';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import { useSmallJobs } from '@core/hooks/useSmallJobs';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { useSelectedCornerRadius } from '@ee-api/corner-radii-api/hooks/useSelectedCornerRadius';
import useMaterials from '@ee-api/materials-api/useMaterials';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { usePlumbing } from '@ee-api/plumbing-api/usePlumbing';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { DrawingShapeContext, usePricedProductsContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { resetSelectedCornerRadius } from '@redux/slices/cornerRadiiSlice';
import { resetSelectedCustomSide } from '@redux/slices/customSideSlice';
import { resetSelectedDiagonalCorner } from '@redux/slices/diagonalCornerSlice';
import { selectIsSavingEstimate } from '@redux/slices/estimateSlice';
import { selectFabricCanvas, selectSelectionCircles, updateSelectionCircles } from '@redux/slices/fabricSlice';

import DevConfigurationComponent from '../DevConfiguration';
import Price from '../price/price';
import AddressInformation from './AddressInformation/AddressInformation';
import BottomSection from './bottom-section/bottom-section';
import { makeDefaultBottomList } from './bottom-section/sections';
import Colors from './colors/colors';
import DesignCountertop from './design-countertop/design-countertop';
import drawingTool from './design-countertop/drawer/drawingTool';
import Materials from './materials/materials';

import './scrollable-area.scss';

const cardStepsDictionary = {
    Information: -1,
    Material: -1,
    Color: -1,
    Design_Countertop: 1,
    Edge: 2,
    Corner_Radius: 3,
    Diagonal_Corner: 4,
    Custom_Side: 5,
    Sink: 6,
    Tear_Out_Countertop: 7,
    CookingAppliance: 8,
    Dishwasher: 9,
    Refrigerator: 10,
    Cutouts: 11,
};

interface ScrollableAreaProps {
    active?: string;
    onCheckoutPressed: () => void;
}

function smoothScrollIntoView(element: RefObject<HTMLDivElement>): void {
    if (element !== null && element.current !== null) {
        element.current.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest',
        });
    }
}

const CustomTrigger = ({ priceAreaCollapsed }: { priceAreaCollapsed: boolean }) => (
    <div className="price-collapse">{priceAreaCollapsed ? <LeftOutlined /> : <RightOutlined />}</div>
);

const BottomSectionWithSteps = ({
    bottomSectionList,
    totalSteps,
    sectionRef,
}: {
    totalSteps: number;
    bottomSectionList: Section[];
    sectionRef: SectionReferences;
}) => (
    <>
        {bottomSectionList.map((step, index) => (
            <div id={step.name} key={index}>
                <BottomSection sectionRef={sectionRef} section={step} totalSteps={totalSteps} />
            </div>
        ))}
    </>
);

const emptyArray = [] satisfies Section[];
const fallbackSectionList = makeDefaultBottomList();

const ScrollableArea = ({ active, onCheckoutPressed }: ScrollableAreaProps): JSX.Element => {
    const topSectionRef = useRef<HTMLDivElement>(null);
    const colorRef = useRef<HTMLDivElement>(null);
    const countertopRef = useRef<HTMLDivElement>(null);
    const materialRef = useRef<HTMLDivElement>(null);
    const informationRef = useRef<HTMLDivElement>(null);

    const { getCardStep } = useCardStep();

    // TODO: fix 'any' type for these refs
    const sectionRef: SectionReferences = {
        edgeRef: useRef(null),
        cornerRadiusRef: useRef(null),
        diagonal_cornersRef: useRef(null),
        custom_sideRef: useRef(null),
        cooking_applianceRef: useRef(null),
        dishwasherRef: useRef(null),
        refrigeratorRef: useRef(null),
        tear_out_countertopRef: useRef(null),
        sinkRef: useRef(null),
        cutoutsRef: useRef(null),
        faucet: useRef(null),
        plumbing: useRef(null),
    };

    const bottomSectionRef = useRef<HTMLDivElement | null>(null);

    const dispatch = useAppDispatch();

    const { selectedValue: selectedCornerRadius } = useSelectedCornerRadius();

    const canvas = useAppSelector(selectFabricCanvas);
    const selectedCornerRadiusOnCard = selectedCornerRadius;

    const isSavingEstimate = useAppSelector(selectIsSavingEstimate);

    // TODO: as a future improvement we'd want to change the 'collapsed'
    // behavior using feature-flags/env-vars
    const [priceAreaCollapsed, setPriceAreaCollapsed] = useState(true);

    const [calculatedHeight, setCalculatedHeight] = useState(0);
    const [, height] = useWindowSize();

    const { selectedValue: selectedStore } = useSelectedStore();

    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const { isFetchingMaterials } = useMaterials();
    const { pricedProducts, priceTotals } = usePricedProductsContext();
    const { useMinQtyAreaForPricing: showMinQtyWarning } = useSmallJobs();
    const { selectedValue: selectedColor } = useSelectedColor();

    const { areThereShapesInTheDrawingArea } = useContext(DrawingShapeContext);
    const { retailerFileSettings } = useRetailerSettings();

    const { plumbingItems } = usePlumbing();

    const { trackViewCart } = useAnalyticsEvents();

    /**
     * Section list to show in the bottom sections with plumbing possibly filtered out
     * THIS IS NOT THE STANDARD WAY OF DEALING WITH SECTIONS BUT AN OBNOXIOUS INCONSISTENT UI EXCEPTION
     * I'm doing this filtering here because it's the "highest" point in the process and should minimize excessive
     * calls to usePlumbing()
     */
    const filteredBottomSections = useMemo(() => {
        const subSections = Object.keys(SectionTypes);

        return (
            retailerFileSettings?.bottomSections.reduce((acc, section) => {
                const validSubsections = section.subSections.filter((subSection) => subSections.includes(subSection));

                if (validSubsections.length) {
                    const newSection = { ...section, stepNumber: acc.length + 2 } as Section; // +2 because Design = 1
                    return [...acc, newSection];
                }
                return acc;
            }, emptyArray as Section[]) ?? fallbackSectionList
        ).filter((section: Section) => {
            return !(section.sectionKey.toLowerCase() === SectionTypes.plumbing && !plumbingItems?.length);
        });
    }, [plumbingItems?.length, retailerFileSettings?.bottomSections]);

    const totalSectionSteps = useMemo(() => filteredBottomSections.length + 1, [filteredBottomSections.length]);

    useLayoutEffect(() => {
        switch (active) {
            case 'color':
                smoothScrollIntoView(colorRef);
                break;
            case 'material':
                smoothScrollIntoView(materialRef);
                break;
            case 'countertop':
                smoothScrollIntoView(countertopRef);
                break;
            case 'information':
                smoothScrollIntoView(informationRef);
                break;
        }
    }, [active]);

    const onCheckoutPressedWithTracking = () => {
        trackViewCart({ pricedProducts, priceTotals });
        onCheckoutPressed();
    };
    const currentSelectionCircles = useAppSelector(selectSelectionCircles);

    // Used to remove the corner radius circles when the corner radius card goes behind the design countertop card or
    // when the page is scrolled down and the corner radius card is not anymore visible
    // eslint-disable-next-line sonarjs/cognitive-complexity
    useLayoutEffect(() => {
        // get the scrollable container
        const scrollableContainer = document.getElementsByClassName('scrollable-container')[0];
        const bottomSection = bottomSectionRef.current;

        if (!scrollableContainer || !bottomSection) {
            return;
        }

        // case 1 the top section is 100%
        const removeSelectionCircles = () => {
            const topSection = document.getElementsByClassName('top-section')[0];
            if (currentSelectionCircles === SELECTION_CIRCLES.FOR_NONE || !topSection || !canvas) {
                return;
            }

            if (topSection.getBoundingClientRect().bottom >= window.innerHeight) {
                drawingTool.removeObjectByName(canvas, CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE);
                dispatch(resetSelectedCustomSide());
                dispatch(resetSelectedDiagonalCorner());
                dispatch(resetSelectedCornerRadius());
            }
        };

        const isSelectedCardVisible = (
            section: HTMLDivElement | null,
            topsBottom: number,
            typeOfSelectionCircle: SELECTION_CIRCLES
        ): boolean => {
            if (!section) {
                return false;
            }
            let inRange = false;
            const selectedSection = section.querySelector('.selected')?.getBoundingClientRect();

            if (selectedSection && topsBottom < selectedSection.bottom && selectedSection.top < window.innerHeight) {
                dispatch(updateSelectionCircles(typeOfSelectionCircle));
                inRange = true;
            } else {
                drawingTool.removeObjectByName(
                    canvas,
                    CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE + '_' + typeOfSelectionCircle
                );
                switch (typeOfSelectionCircle) {
                    case SELECTION_CIRCLES.FOR_CUSTOM_SIDES:
                        dispatch(resetSelectedCustomSide());
                        break;
                    case SELECTION_CIRCLES.FOR_DIAGONAL_CORNERS:
                        dispatch(resetSelectedDiagonalCorner());
                        break;
                    case SELECTION_CIRCLES.FOR_ROUNDED_RADII:
                        dispatch(resetSelectedCornerRadius());
                        break;
                }
            }
            return inRange;
        };

        // case 2 the corner radius card disappear from the screen to the bottom
        // case 3 the corner radius card disappear from the screen to the top
        const removeSelectionCirclesOnBottomSectionScroll = () => {
            if (
                currentSelectionCircles === SELECTION_CIRCLES.FOR_NONE ||
                canvas === null ||
                canvas.getObjects() === null
            ) {
                return;
            }
            const topSection = document.getElementsByClassName('top-section')[0];
            const topSectionBottom = topSection.getBoundingClientRect().bottom;
            const customSidesInRange = isSelectedCardVisible(
                sectionRef.custom_sideRef.current,
                topSectionBottom,
                SELECTION_CIRCLES.FOR_CUSTOM_SIDES
            );
            const diagonalCornersInRange = isSelectedCardVisible(
                sectionRef.diagonal_cornersRef.current,
                topSectionBottom,
                SELECTION_CIRCLES.FOR_DIAGONAL_CORNERS
            );
            const roundedRadiiInRange = isSelectedCardVisible(
                sectionRef.cornerRadiusRef.current,
                topSectionBottom,
                SELECTION_CIRCLES.FOR_ROUNDED_RADII
            );
            if (!(customSidesInRange || diagonalCornersInRange || roundedRadiiInRange)) {
                dispatch(updateSelectionCircles(SELECTION_CIRCLES.FOR_NONE));
                drawingTool.removeObjectByName(canvas, CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE);
            }
        };

        scrollableContainer.addEventListener('scroll', removeSelectionCircles, { passive: true });
        bottomSection.addEventListener('scroll', removeSelectionCirclesOnBottomSectionScroll, { passive: true });

        return () => {
            scrollableContainer.removeEventListener('scroll', removeSelectionCircles);
            bottomSection.removeEventListener('scroll', removeSelectionCirclesOnBottomSectionScroll);
        };
    }, [
        currentSelectionCircles,
        sectionRef.cornerRadiusRef,
        sectionRef.diagonal_cornersRef,
        sectionRef.custom_sideRef,
        canvas,
        selectedCornerRadiusOnCard,
        dispatch,
    ]);

    const movePricingArea = (): void => {
        const pricingArea = document.querySelector('.price-area');

        if (
            !pricingArea ||
            pricingArea.getBoundingClientRect().width === 0 ||
            !topSectionRef ||
            !topSectionRef.current
        ) {
            return;
        }

        const topSectionDimensions = topSectionRef.current.getBoundingClientRect();
        const pricingAreaNewHeight = topSectionDimensions.height + topSectionDimensions.top;
        const marginHeight = 20;

        pricingAreaNewHeight <= height
            ? setCalculatedHeight(pricingAreaNewHeight - marginHeight)
            : setCalculatedHeight(height);
    };

    useLayoutEffect(() => {
        movePricingArea();
    }, [priceAreaCollapsed]);

    const triggerCollapse = () => {
        if (!isFetchingMaterials) {
            setPriceAreaCollapsed(!priceAreaCollapsed);
        }
    };

    const onCollapse = (collapsed: boolean) => {
        setPriceAreaCollapsed(collapsed);
    };

    const CheckoutSection = () => {
        const showCheckoutButton = areThereShapesInTheDrawingArea && selectedMaterial;

        return showCheckoutButton ? (
            <div className="checkout-section">
                <CheckoutButton
                    label={'SCROLLABLE_AREA.REVIEW_ORDER'}
                    onCheckoutPressed={onCheckoutPressedWithTracking}
                    disabled={isSavingEstimate}
                />
            </div>
        ) : null;
    };

    const [isBottomSectionExpanded, setIsBottomSectionExpanded] = useState(false);

    const hideTopSections = !selectedStore || !selectedColor?.productId;
    const hideBottomSections = hideTopSections || !areThereShapesInTheDrawingArea;
    const showPageLoader = !!selectedStore && isFetchingMaterials;

    const showDevConfig = isDevMode();

    return (
        <div id="EstimatePage" className="scrollable-container" onScroll={movePricingArea}>
            <div className="top-section" ref={topSectionRef}>
                <div className={priceAreaCollapsed ? 'top-section__cards--full' : 'top-section__cards'}>
                    {showDevConfig && (
                        <div id="dev-config">
                            <DevConfigurationComponent />
                        </div>
                    )}
                    <div id="information" ref={informationRef}>
                        <AddressInformation
                            step={cardStepsDictionary['Information']}
                            stepInfo={''}
                            emphasis={hideTopSections}
                        />
                    </div>
                    <div id="material" ref={materialRef}>
                        <Materials step={cardStepsDictionary['Material']} stepInfo={''} disabled={hideTopSections} />
                    </div>

                    <div id="color" ref={colorRef} className={classNames({ 'dim-section': hideTopSections })}>
                        <Colors step={cardStepsDictionary['Color']} stepInfo={''} disabled={hideTopSections} />
                    </div>

                    {showPageLoader && <PageLoader />}

                    <div
                        id="countertop"
                        ref={countertopRef}
                        className={classNames({ 'dim-section': hideTopSections, disabled: hideTopSections })}
                    >
                        <DesignCountertop
                            stepInfo={getCardStep(cardStepsDictionary['Design_Countertop'], totalSectionSteps)}
                        />
                    </div>
                </div>
                {priceAreaCollapsed ? (
                    <PriceBubbleButton
                        price={priceTotals.priceWithDiscount}
                        showMinimumWarning={showMinQtyWarning}
                        color={selectedColor}
                        onClick={() => triggerCollapse()}
                    />
                ) : (
                    <div className="price-area-wrapper">
                        <Sider
                            className="price-area"
                            onCollapse={onCollapse}
                            theme="light"
                            breakpoint="sm"
                            collapsedWidth={0}
                            collapsible
                            trigger={<CustomTrigger priceAreaCollapsed={priceAreaCollapsed} />}
                            reverseArrow
                            style={{
                                height: `${calculatedHeight !== 0 ? calculatedHeight + 'px' : '100vh'}`,
                            }}
                        >
                            <Price onCheckoutPressed={onCheckoutPressedWithTracking} />
                        </Sider>
                    </div>
                )}
            </div>

            <div className={classNames('ScrollableArea__expansion-container', { 'dim-section': hideBottomSections })}>
                <div
                    className={classNames('ScrollableArea__expansion-panel', {
                        'ScrollableArea__expansion-panel--expanded': isBottomSectionExpanded && !hideBottomSections,
                    })}
                >
                    {isBottomSectionExpanded ? (
                        <FullscreenExitOutlined
                            className="ScrollableArea__expansion-panel-icon"
                            onClick={() => setIsBottomSectionExpanded(false)}
                            hidden={hideBottomSections}
                        />
                    ) : (
                        <FullscreenOutlined
                            className="ScrollableArea__expansion-panel-icon"
                            onClick={() => setIsBottomSectionExpanded(true)}
                            hidden={hideBottomSections}
                        />
                    )}
                    <div
                        ref={bottomSectionRef}
                        className={classNames('ScrollableArea__bottom-section', { 'dim-section': hideBottomSections })}
                    >
                        <BottomSectionWithSteps
                            bottomSectionList={filteredBottomSections}
                            sectionRef={sectionRef}
                            totalSteps={totalSectionSteps}
                        />
                        <CheckoutSection />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScrollableArea;
